import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PortalModal from '@/components/ui/PortalModal';
import { 
  UserPlus, Search, Phone, Mail, Calendar, CheckCircle, 
  Clock, AlertCircle, X, Send, Loader2, RefreshCw, 
  ChevronDown, ChevronUp, FileText, MessageSquare, Check,
  ExternalLink, User, Building2
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

export default function ReferralsDashboard() {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState('outstanding'); // Default to outstanding only
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  
  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Status update state
  const [updatingStatus, setUpdatingStatus] = useState(null);

  const fetchReferrals = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [referralsRes, statsRes] = await Promise.all([
        axios.get(`${API}/referrals`, { headers }),
        axios.get(`${API}/referrals/stats`, { headers })
      ]);
      
      setReferrals(referralsRes.data.referrals || []);
      setStats(statsRes.data || {});
    } catch (err) {
      console.error('Failed to fetch referrals:', err);
      toast.error('Failed to load referrals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  const updateStatus = async (referralId, newStatus) => {
    setUpdatingStatus(referralId);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/referrals/${referralId}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Status updated to ${newStatus}`);
      fetchReferrals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const openEmailModal = (referral) => {
    setSelectedReferral(referral);
    setEmailSubject(`Following up on your referral to Elite Player Performance`);
    setEmailMessage(`Hi ${referral.first_name},

Thank you for your interest in Elite Player Performance. I wanted to reach out personally to discuss your training goals and answer any questions you might have.

Would you be available for a quick call this week? Please let me know a time that works for you, or feel free to reply to this email with any questions.

Looking forward to hearing from you!`);
    setShowEmailModal(true);
  };

  const sendEmail = async () => {
    if (!selectedReferral || !emailSubject.trim() || !emailMessage.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }
    
    setSendingEmail(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/referrals/${selectedReferral.id}/send-email`,
        null,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { subject: emailSubject, message: emailMessage }
        }
      );
      toast.success(`Email sent to ${selectedReferral.first_name}`);
      setShowEmailModal(false);
      setSelectedReferral(null);
      setEmailSubject('');
      setEmailMessage('');
      fetchReferrals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'contacted': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'scheduled': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'converted': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'declined': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'contacted': return <Phone className="h-4 w-4" />;
      case 'scheduled': return <Calendar className="h-4 w-4" />;
      case 'converted': return <CheckCircle className="h-4 w-4" />;
      case 'declined': return <X className="h-4 w-4" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  // Outstanding = not completed (converted) or declined
  const outstandingStatuses = ['pending', 'contacted', 'scheduled'];
  
  const filteredReferrals = referrals.filter(ref => {
    // Filter logic
    let matchesFilter;
    if (filter === 'all') {
      matchesFilter = true;
    } else if (filter === 'outstanding') {
      matchesFilter = outstandingStatuses.includes(ref.status);
    } else if (filter === 'completed') {
      matchesFilter = ref.status === 'converted' || ref.status === 'declined';
    } else {
      matchesFilter = ref.status === filter;
    }
    
    const matchesSearch = !searchQuery || 
      `${ref.first_name} ${ref.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ref.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ref.phone?.includes(searchQuery);
    return matchesFilter && matchesSearch;
  });
  
  // Count outstanding referrals
  const outstandingCount = referrals.filter(r => outstandingStatuses.includes(r.status)).length;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  return (
    <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 p-6" data-testid="referrals-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <UserPlus className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Referrals to Contact</h2>
            <p className="text-sm text-zinc-500">
              <span className="text-emerald-400 font-medium">{outstandingCount} outstanding</span>
              {' · '}{stats.total || 0} total
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchReferrals}
          className="text-emerald-400 hover:text-emerald-300"
          data-testid="refresh-referrals-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-amber-400">{stats.pending || 0}</div>
          <div className="text-xs text-amber-400/70">Pending</div>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-blue-400">{stats.contacted || 0}</div>
          <div className="text-xs text-blue-400/70">Contacted</div>
        </div>
        <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-purple-400">{stats.scheduled || 0}</div>
          <div className="text-xs text-purple-400/70">Scheduled</div>
        </div>
        <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-green-400">{stats.converted || 0}</div>
          <div className="text-xs text-green-400/70">Converted</div>
        </div>
        <div className="bg-zinc-500/10 border border-zinc-500/20 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-zinc-400">{stats.recent_30_days || 0}</div>
          <div className="text-xs text-zinc-400/70">Last 30 Days</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, email, or phone..."
            className="pl-10 bg-zinc-800 border-zinc-700 text-white"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
        >
          <option value="outstanding">Outstanding ({outstandingCount})</option>
          <option value="all">All Referrals</option>
          <option value="pending">Pending</option>
          <option value="contacted">Contacted</option>
          <option value="scheduled">Scheduled</option>
          <option value="completed">Completed/Declined</option>
        </select>
      </div>

      {/* Referrals List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
        </div>
      ) : filteredReferrals.length === 0 ? (
        <div className="text-center py-12">
          <UserPlus className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-500">No referrals found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredReferrals.map((referral) => (
            <div 
              key={referral.id}
              className="bg-zinc-800/50 border border-zinc-700 rounded-xl overflow-hidden"
            >
              {/* Main Row */}
              <div className="p-4 flex items-center gap-4">
                {/* Avatar */}
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shrink-0">
                  <span className="text-white font-bold text-lg">
                    {referral.first_name?.[0]}{referral.last_name?.[0]}
                  </span>
                </div>
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-white">
                      {referral.first_name} {referral.last_name}
                    </h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full border flex items-center gap-1 ${getStatusColor(referral.status)}`}>
                      {getStatusIcon(referral.status)}
                      {referral.status}
                    </span>
                    {referral.partner_name && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        {referral.partner_name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-sm text-zinc-400">
                    <span className="flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      {referral.email}
                    </span>
                    <span className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {referral.phone}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(referral.created_at)}
                    </span>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2 shrink-0">
                  {/* Quick Status Buttons */}
                  {referral.status === 'pending' && (
                    <Button
                      size="sm"
                      onClick={() => updateStatus(referral.id, 'contacted')}
                      disabled={updatingStatus === referral.id}
                      className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/30"
                      title="Mark as Contacted"
                    >
                      {updatingStatus === referral.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                      <span className="ml-1 hidden sm:inline">Contacted</span>
                    </Button>
                  )}
                  
                  {referral.status === 'contacted' && (
                    <Button
                      size="sm"
                      onClick={() => updateStatus(referral.id, 'converted')}
                      disabled={updatingStatus === referral.id}
                      className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30"
                      title="Mark as Completed/Converted"
                    >
                      {updatingStatus === referral.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle className="h-4 w-4" />
                      )}
                      <span className="ml-1 hidden sm:inline">Complete</span>
                    </Button>
                  )}
                  
                  {/* Email Button */}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEmailModal(referral)}
                    className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                    title="Send Email"
                  >
                    <Mail className="h-4 w-4" />
                  </Button>
                  
                  {/* Phone Link */}
                  <a
                    href={`tel:${referral.phone}`}
                    className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors"
                    title="Call"
                  >
                    <Phone className="h-4 w-4" />
                  </a>
                  
                  {/* Expand Button */}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setExpandedId(expandedId === referral.id ? null : referral.id)}
                    className="text-zinc-400"
                  >
                    {expandedId === referral.id ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Expanded Details */}
              {expandedId === referral.id && (
                <div className="border-t border-zinc-700 p-4 bg-zinc-900/50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                        <FileText className="h-4 w-4 text-emerald-400" />
                        Referral Details
                      </h4>
                      <div className="space-y-2 text-sm">
                        <p className="text-zinc-400">
                          <span className="text-zinc-500">DOB:</span> {formatDate(referral.date_of_birth)}
                        </p>
                        <p className="text-zinc-400">
                          <span className="text-zinc-500">Reason:</span> {referral.reason_for_referral}
                        </p>
                        {referral.additional_notes && (
                          <p className="text-zinc-400">
                            <span className="text-zinc-500">Notes:</span> {referral.additional_notes}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {(referral.referrer_name || referral.referrer_organization) && (
                      <div>
                        <h4 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                          <User className="h-4 w-4 text-emerald-400" />
                          Referred By
                        </h4>
                        <div className="space-y-2 text-sm">
                          {referral.referrer_name && (
                            <p className="text-zinc-400">
                              <span className="text-zinc-500">Name:</span> {referral.referrer_name}
                            </p>
                          )}
                          {referral.referrer_organization && (
                            <p className="text-zinc-400">
                              <span className="text-zinc-500">Organization:</span> {referral.referrer_organization}
                            </p>
                          )}
                          {referral.referrer_email && (
                            <p className="text-zinc-400">
                              <span className="text-zinc-500">Email:</span> {referral.referrer_email}
                            </p>
                          )}
                          {referral.referrer_phone && (
                            <p className="text-zinc-400">
                              <span className="text-zinc-500">Phone:</span> {referral.referrer_phone}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Status History */}
                  {referral.contacted_at && (
                    <div className="mt-4 pt-4 border-t border-zinc-700">
                      <p className="text-xs text-zinc-500">
                        Contacted on {formatDate(referral.contacted_at)} by {referral.contacted_by_name || 'Staff'}
                      </p>
                    </div>
                  )}
                  
                  {/* Status Actions */}
                  <div className="mt-4 pt-4 border-t border-zinc-700 flex flex-wrap gap-2">
                    <span className="text-xs text-zinc-500 self-center mr-2">Change status:</span>
                    {['pending', 'contacted', 'scheduled', 'converted', 'declined'].map(status => (
                      <button
                        key={status}
                        onClick={() => updateStatus(referral.id, status)}
                        disabled={updatingStatus === referral.id || referral.status === status}
                        className={`text-xs px-3 py-1 rounded-full border transition-all ${
                          referral.status === status 
                            ? getStatusColor(status) + ' opacity-100'
                            : 'border-zinc-600 text-zinc-500 hover:border-zinc-500 hover:text-zinc-400'
                        } disabled:opacity-50`}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Email Modal */}
      <PortalModal isOpen={showEmailModal} onClose={() => setShowEmailModal(false)} maxWidth="max-w-xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Mail className="h-5 w-5 text-emerald-400" />
            <h3 className="text-lg font-bold text-white">
              Send Email to {selectedReferral?.first_name}
            </h3>
          </div>
          <button onClick={() => setShowEmailModal(false)} className="text-zinc-500 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">To</label>
            <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-400">
              {selectedReferral?.email}
            </div>
          </div>
          
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">Subject *</label>
            <Input
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              placeholder="Email subject..."
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">Message *</label>
            <textarea
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              placeholder="Write your message..."
              rows={8}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white resize-none"
            />
          </div>
        </div>
        
        <div className="flex gap-3 mt-6">
          <Button
            variant="outline"
            onClick={() => setShowEmailModal(false)}
            className="flex-1 border-zinc-700"
          >
            Cancel
          </Button>
          <Button
            onClick={sendEmail}
            disabled={sendingEmail || !emailSubject.trim() || !emailMessage.trim()}
            className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 text-white"
          >
            {sendingEmail ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Send Email
          </Button>
        </div>
      </PortalModal>
    </div>
  );
}
